export default defineNuxtRouteMiddleware(async (to) => {
  const { isDemoMode, enableDemo, ensureMSWRunning } = useDemo()

  // Handle demo mode activation via query param
  // Keep the demo param in URL until MSW is fully ready to avoid race conditions
  if (to.query.demo === 'true') {
    // Enable demo mode and keep navigating with demo param
    // until MSW is ready and session is loaded
    if (!isDemoMode.value) {
      await enableDemo()
    }

    // Ensure MSW is running and fetch demo session
    await ensureMSWRunning()
    const auth = useAuth()
    if (!auth.user.value) {
      try {
        await auth.checkAuthStatus()
      } catch {
        // Ignore errors in demo mode - user data will use fallbacks
      }
    }

    // Now that demo mode is fully set up, strip the query param
    if (isDemoMode.value && auth.user.value) {
      return navigateTo(to.path, { replace: true })
    }
    return
  }

  // If in demo mode (from sessionStorage), ensure MSW is running and fetch session data
  if (isDemoMode.value) {
    await ensureMSWRunning()

    // Fetch demo user session if not already loaded
    const auth = useAuth()
    if (!auth.user.value) {
      try {
        await auth.checkAuthStatus()
      } catch {
        // Ignore errors in demo mode - user data will use fallbacks
      }
    }
    return
  }

  const auth = useAuth()
  const { isAuthenticated: checkAdminAuthenticated, checkAdminSession } = useAdminAuth()

  const publicPages = ['/login', '/forgot-password', '/reset-password', '/gallery', '/privacy']
  const isPublicPage = publicPages.includes(to.path) || to.path.startsWith('/auth/') || to.path.startsWith('/admin') || to.path.startsWith('/gallery')

  let authenticated = auth.isAuthenticated.value
  const hasSessionData = !!auth.user.value || !!auth.userClaims.value

  // Check for admin authentication (cached state first, then server check)
  let isAdminAuthenticated = checkAdminAuthenticated()

  if (!authenticated && !hasSessionData && !isAdminAuthenticated) {
    // Verify admin session with server
    try {
      isAdminAuthenticated = await checkAdminSession()
    } catch {
      // ignore - will try merchant auth next
    }

    // If still not admin-authenticated, try merchant auth
    if (!isAdminAuthenticated) {
      try {
        authenticated = await auth.checkAuthStatus()
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((error as any).statusCode && (error as any).statusCode !== 401) {
          console.error('Auth middleware failed to verify session:', error)
        }
        authenticated = false
      }
    }
  }

  // Redirect authenticated users away from auth pages (but not gallery)
  const isAuthPage = ['/login', '/forgot-password', '/reset-password'].includes(to.path)
  if ((authenticated || isAdminAuthenticated) && isAuthPage) {
    return navigateTo('/', { replace: true })
  }

  if (!authenticated && !isAdminAuthenticated && !isPublicPage) {
    return navigateTo('/login', { replace: true })
  }
})
